import * as signalR from '@microsoft/signalr';
import { t } from 'i18next';
import { Constants } from '../../../Constants';

import { AlertType } from '../../../libs/models/AlertType';
import {
    IChatMessage,
    isAgentSessionActive,
    newestHandoverNotice,
    SystemNoticeKind,
} from '../../../libs/models/ChatMessage';
import { ChatState } from '../conversations/ChatState';
import { BackendServiceUrl, BlockedTokenResponseBody } from '../../../libs/services/BaseService';
import { isHandoverEligible } from '../../../libs/utils/handoverEligibility';
import { AppStore, StoreMiddlewareAPI } from '../../app/store';
import { addAlert, removeAlertWithId } from '../app/appSlice';
import {
    mergeConversationMessages,
    updateBotResponseStatus,
    updateMessageProperty,
} from '../conversations/conversationsSlice';
import { setEscalationOffer } from '../livechat/escalationOfferSlice';
import { setAgentTyping } from '../livechat/agentTypingSlice';
import { setScreenshotRequest } from '../livechat/screenshotRequestSlice';
import { AgentImagePush, buildAgentImageMessage } from '../../../libs/utils/localImageEcho';
import { AuthHelper, AuthType } from '../../../libs/auth/AuthHelper';
import { OidcAuthHelper } from '../../../libs/auth/OidcAuthHelper';

/*
 * This is a module that encapsulates the SignalR connection
 * to the messageRelayHub on the server.
 */

// These have to match the callback names used in the backend
const enum SignalRCallbackMethods {
    ReceiveMessage = 'ReceiveMessage',
    ReceiveMessageUpdate = 'ReceiveMessageUpdate',
    ReceiveAgentTypingState = 'ReceiveAgentTypingState',
    ReceiveScreenshotRequest = 'ReceiveScreenshotRequest',
    ReceiveAgentImage = 'ReceiveAgentImage',
    ReceiveBotResponseStatus = 'ReceiveBotResponseStatus',
    ReceiveEscalationOffer = 'ReceiveEscalationOffer',
    SessionLoggedOut = 'SessionLoggedOut',
}

const tokenFactory = (store: StoreMiddlewareAPI) => async () => {
    const token = store.getState().app.authToken ?? '';
    console.log('tokenFactory, token exists:', !!token);
    return Promise.resolve(token);
};

// The provider rides in the query string, not a header: the backend reads it from the query for
// everything under the hub path, because a browser WebSocket client cannot set custom headers
// (see OidcProviderSelector).
const buildHubUrl = (store: StoreMiddlewareAPI) => {
    const hubUrl = new URL('/messageRelayHub', BackendServiceUrl);
    const provider = OidcAuthHelper.getProviderConfig(store as AppStore);
    hubUrl.searchParams.append('oidcprovider', provider.providerName);
    return hubUrl;
};

const PROBE_TIMEOUT_MS = 5000;

/**
 * What the backend says about the token this connection was using.
 *
 *  - `revoked`: back-channel logout blocked the session; the user has to be logged out.
 *  - `unauthorized`: any other 401 — the token expired or is no longer accepted. The server closes
 *    the socket at the token's expiry (CloseOnAuthenticationExpiration), so this is an expected
 *    outcome, not a network problem.
 *  - `accepted`: the token still works, so whatever broke the connection lies elsewhere.
 *  - `unknown`: we could not ask.
 */
export type SessionProbeOutcome = 'revoked' | 'unauthorized' | 'accepted' | 'unknown';

/**
 * Asks the backend what it makes of this connection's token, on the hub's own path.
 *
 * The hub connects with skipNegotiation over a raw WebSocket, and the browser exposes neither the
 * status nor the body of a failed upgrade to JS. BaseService's blocked-token branch — the thing that
 * normally triggers the logout — therefore can never fire for hub traffic, so a revoked user used to
 * sit with a generic "connection lost" alert and reconnect attempts until some unrelated REST call
 * happened to run (#2175). This re-asks with fetch, where the 401 and its body are readable. A plain
 * GET never allocates a SignalR connection, and the token goes in the Authorization header rather
 * than the query string so the probe does not put another copy of it in a URL.
 *
 * Exported for the tests that pin the classification; callers go through
 * {@link handleSessionProbeOutcome}.
 */
export const probeSessionState = async (store: StoreMiddlewareAPI): Promise<SessionProbeOutcome> => {
    const token = store.getState().app.authToken;
    if (!token) {
        return 'unknown';
    }

    // AbortController rather than AbortSignal.timeout: the latter is unavailable on browsers still
    // inside our browserslist range (and in jsdom), where it would throw and turn this into the
    // catch below — silently skipping the logout it exists to trigger.
    const controller = new AbortController();
    const timeout = setTimeout(() => {
        controller.abort();
    }, PROBE_TIMEOUT_MS);

    try {
        const response = await fetch(buildHubUrl(store), {
            method: 'GET',
            headers: { Authorization: `Bearer ${token}` },
            // The disconnect alert waits on this answer, so a backend that accepts the socket but
            // never replies must not swallow the user's "connection lost" feedback outright.
            signal: controller.signal,
            // This is the one probe carrying a bearer token; refuse to replay it to a redirect
            // target rather than following one (same reasoning as #1923).
            redirect: 'error',
        });

        if (response.status !== 401) {
            return 'accepted';
        }

        return (await response.text()) === BlockedTokenResponseBody ? 'revoked' : 'unauthorized';
    } catch (probeError) {
        // Best-effort by design: if the backend cannot be reached at all then the connection failure
        // really is a network problem, and the caller's normal alert is the right outcome.
        console.error('Could not determine whether the session was revoked:', probeError);
        return 'unknown';
    } finally {
        clearTimeout(timeout);
    }
};

const alertSessionEnded = (store: StoreMiddlewareAPI) => {
    store.dispatch(
        addAlert({
            message: t('signalR.sessionEnded'),
            type: AlertType.Error,
            id: Constants.app.CONNECTION_ALERT_ID,
        }),
    );
};

/**
 * Acts on a probe result. Resolves true when the user has been dealt with — logged out, or told that
 * their session ended — so the caller does not also report a network problem. Exported for tests.
 */
export const handleSessionProbeOutcome = async (
    store: StoreMiddlewareAPI,
    outcome: SessionProbeOutcome,
): Promise<boolean> => {
    if (outcome === 'revoked' && (await AuthHelper.logoutForBlockedToken())) {
        return true;
    }

    // Either the token is simply no longer accepted, or it was revoked and no logout function was
    // registered to act on it. Both leave the user with a session they cannot use, and neither is the
    // network fault the generic alert describes.
    if (outcome === 'revoked' || outcome === 'unauthorized') {
        alertSessionEnded(store);
        return true;
    }

    return false;
};

// SignalR's default schedule (0, 2, 10, 30 s) gives up after ~42 s, which is shorter than a backend
// restart during a deploy: the customer was left on a permanent "disconnected" alert until they
// reloaded. This one keeps trying for ~2.5 min before onclose takes over.
const RECONNECT_DELAYS_MS = [0, 2_000, 5_000, 10_000, 15_000, 20_000, 30_000, 30_000, 30_000];

// The floor gives the immediate first retry a spread too; 25% of 0 would leave it in lockstep.
const MIN_JITTER_MS = 500;

/**
 * Every open tab drops at the same moment when the backend restarts, so up to 25% jitter (at least
 * MIN_JITTER_MS) spreads their reconnects instead of landing them on the freshly started instance in
 * lockstep. Null once the window is used up.
 */
const nextReconnectDelayMs = (previousRetryCount: number): number | null => {
    if (previousRetryCount >= RECONNECT_DELAYS_MS.length) {
        return null;
    }
    const delay = RECONNECT_DELAYS_MS[previousRetryCount];
    return delay + Math.floor(Math.random() * Math.max(delay * 0.25, MIN_JITTER_MS));
};

/** Exported for tests. */
export const reconnectRetryPolicy: signalR.IRetryPolicy = {
    nextRetryDelayInMilliseconds: ({ previousRetryCount }) => nextReconnectDelayMs(previousRetryCount),
};

const isRunInFlight = (conversation: ChatState): boolean =>
    conversation.disabled || conversation.botResponseStatus !== undefined || conversation.activeRunId !== undefined;

/**
 * Whether the selected chat can have missed server-pushed messages while the socket was down, in a
 * way local state can tell. The server does not replay what it sent during the gap (#2175):
 *
 *  - A run in flight loses streamed ReceiveMessageUpdate chunks and the terminal
 *    ReceiveBotResponseStatus, leaving a half-rendered answer with a status that never clears.
 *  - An active agent session loses the agent's ReceiveMessage pushes, which stay invisible until a
 *    reload.
 *
 * An idle AI chat has nothing to recover, so it is not re-fetched. Exported for tests.
 */
export const needsBackfillAfterReconnect = (conversation: ChatState): boolean =>
    isRunInFlight(conversation) || isAgentSessionActive(conversation.messages);

const HANDOVER_ENDING_NOTICES: readonly SystemNoticeKind[] = ['ChatResolved', 'QueueTimedOut', 'ChatClosedByCustomer'];

const newestNoticeEndsHandover = (messages: IChatMessage[]): boolean => {
    const notice = newestHandoverNotice(messages);
    return notice !== undefined && HANDOVER_ENDING_NOTICES.includes(notice);
};

/**
 * The cases local state cannot see, because the notice that would show them never arrived:
 *
 *  - `Human`: an agent claimed the chat while the socket was down, so it still looks like an AI chat,
 *    or a queued one.
 *  - `Resolved`: the handover began and ended inside the gap. The agent claimed, wrote and resolved,
 *    the queue timed out, or another tab closed the chat. The mode stays `Resolved` afterwards, so a
 *    chat whose newest notice already ends a handover is skipped; otherwise every reconnect of a chat
 *    that ever had one would re-fetch.
 *
 * Only asked when live chat is on and the customer is eligible for a handover; everyone else would
 * get the concealing 404 on every reconnect. Exported for tests.
 */
export const missedHandoverWhileOfflineAsync = async (
    store: StoreMiddlewareAPI,
    chatId: string,
    messages: IChatMessage[],
): Promise<boolean> => {
    const state = store.getState();
    if (!state.app.serviceInfo.featureFlags.LiveChatEnabled) {
        return false;
    }
    try {
        // Inside the try: the host context arrives from the embedding page, and a malformed product
        // list must not escape into the group-join error handler below.
        if (!isHandoverEligible(state.hostContext.hostContext)) {
            return false;
        }
        // Lazy for the same reason as ChatService in the backfill below: CustomerLiveChatService
        // extends BaseService.
        const { CustomerLiveChatService } = await import('../../../libs/services/CustomerLiveChatService');
        const { mode } = await new CustomerLiveChatService(state.app.authToken ?? '').getModeAsync(
            chatId,
            state.hostContext.hostContext,
        );
        return mode === 'Human' || (mode === 'Resolved' && !newestNoticeEndsHandover(messages));
    } catch {
        // Not eligible (the endpoint conceals itself with a 404) or not reachable: nothing to recover.
        return false;
    }
};

const alertConnected = (store: StoreMiddlewareAPI) => {
    store.dispatch(
        addAlert({ message: t('signalR.connected'), type: AlertType.Success, id: Constants.app.CONNECTION_ALERT_ID }),
    );
    setTimeout(() => {
        // A drop within these 7.5 s puts a "reconnecting" alert under the same id, and the customer must
        // keep seeing that one while the client retries.
        const current = store.getState().app.alerts.find((alert) => alert.id === Constants.app.CONNECTION_ALERT_ID);
        if (current?.type === AlertType.Success) {
            store.dispatch(removeAlertWithId(Constants.app.CONNECTION_ALERT_ID));
        }
    }, 7500);
};

/**
 * Re-fetches a chat that may have missed pushes while no socket was up. Call it only after the chat's
 * group join has succeeded, so anything pushed after the fetch still arrives live. Never rejects.
 *
 * @param agentMessagesOnly - Recover missed agent messages only and leave the run status alone. On the
 * start path a run in flight is usually the one whose send brought the connection up, still generating;
 * clearing its status would drop the stop button and, via the not-in-flight guard, every later status.
 */
const backfillMissedMessagesAsync = async (store: StoreMiddlewareAPI, chatId: string, agentMessagesOnly = false) => {
    try {
        const conversations = store.getState().conversations.conversations as Record<string, ChatState | undefined>;
        const conversation = conversations[chatId];
        if (!conversation) {
            return;
        }
        // Only a run in flight now can have lost its terminal status. The customer can start another one
        // during the round trips below (a queue timeout in the gap lets sends through again), and that
        // one's status must survive.
        const clearsRunStatus = !agentMessagesOnly && isRunInFlight(conversation);
        const runIdAtCheck = conversation.activeRunId;
        const localStateSaysMissed = agentMessagesOnly
            ? isAgentSessionActive(conversation.messages)
            : needsBackfillAfterReconnect(conversation);
        if (!localStateSaysMissed && !(await missedHandoverWhileOfflineAsync(store, chatId, conversation.messages))) {
            return;
        }

        // Imported lazily to keep ChatService out of this module's static import graph. A top-level
        // import closes the cycle BaseService → store → signalR middleware → this module → ChatService →
        // BaseService; because ChatService `extends BaseService` at module-eval time, any suite whose
        // entry point sits earlier in the chain evaluates that `extends` before BaseService is
        // initialised and dies with "Class extends value undefined" (#2175).
        const { ChatService } = await import('../../../libs/services/ChatService');
        const chatService = new ChatService(store.getState().app.authToken ?? '');
        const messages = await chatService.getChatMessagesAsync(chatId, 0, 100, conversation.hostContext);
        store.dispatch(mergeConversationMessages({ chatId, messages }));
        const current = (store.getState().conversations.conversations as Record<string, ChatState | undefined>)[chatId];
        if (clearsRunStatus && current !== undefined && current.activeRunId === runIdAtCheck) {
            store.dispatch(updateBotResponseStatus({ chatId, status: undefined }));
        }
    } catch (backfillError: unknown) {
        console.error('Failed to backfill messages after reconnect:', backfillError);
    }
};

// Set up a SignalR connection to the messageRelayHub on the server
const setupSignalRConnectionToChatHub = (store: StoreMiddlewareAPI) => {
    const connectionHubUrl = buildHubUrl(store);

    const signalRConnectionOptions = {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
        logger: signalR.LogLevel.Warning,
        accessTokenFactory: tokenFactory(store),
    };

    // Create the connection instance
    // withAutomaticReconnect will automatically try to reconnect and generate a new socket connection if needed
    const hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(connectionHubUrl.toString(), signalRConnectionOptions)
        .withAutomaticReconnect(reconnectRetryPolicy)
        .withHubProtocol(new signalR.JsonHubProtocol())
        .configureLogging(signalR.LogLevel.Information)
        .build();

    // Note: to keep the connection open the serverTimeout should be
    // larger than the KeepAlive value that is set on the server
    // keepAliveIntervalInMilliseconds default is 15000 and we are using default
    // serverTimeoutInMilliseconds default is 30000 and we are using 60000 set below
    hubConnection.serverTimeoutInMilliseconds = 60000;

    return hubConnection;
};

const registerCommonSignalConnectionEvents = (hubConnection: signalR.HubConnection, store: StoreMiddlewareAPI) => {
    // Re-establish the connection if connection dropped
    hubConnection.onclose((error) => {
        if (hubConnection.state === signalR.HubConnectionState.Disconnected) {
            // The server closes this connection on purpose when the session is revoked
            // (TokenDenyHubFilter) or the token expires (CloseOnAuthenticationExpiration). Neither
            // reason survives the WebSocket close, so ask before blaming the network (#2175).
            const reportDisconnected = () => {
                const errorMessage = t('signalR.disconnected');
                store.dispatch(
                    addAlert({
                        message: errorMessage,
                        type: AlertType.Error,
                        id: Constants.app.CONNECTION_ALERT_ID,
                    }),
                );
                console.log(errorMessage, error);
            };

            void probeSessionState(store)
                .then(async (outcome) => {
                    if (!(await handleSessionProbeOutcome(store, outcome))) {
                        reportDisconnected();
                    }
                })
                // The user is disconnected either way, so an unexpected failure in the revocation
                // check must still surface the disconnect rather than end as an unhandled rejection.
                .catch((probeError: unknown) => {
                    console.error('Revocation check failed after disconnect:', probeError);
                    reportDisconnected();
                });
        }
    });

    hubConnection.onreconnecting((error) => {
        if (hubConnection.state === signalR.HubConnectionState.Reconnecting) {
            const errorMessage = t('signalR.reconnecting');
            store.dispatch(
                addAlert({
                    message: errorMessage,
                    type: AlertType.Info,
                    id: Constants.app.CONNECTION_ALERT_ID,
                }),
            );
            console.log(errorMessage, error);
        }
    });

    hubConnection.onreconnected((connectionId = '') => {
        if (hubConnection.state === signalR.HubConnectionState.Connected) {
            const selectedId = store.getState().conversations.selectedId;
            if (selectedId) {
                hubConnection
                    .invoke('AddClientToGroupAsync', selectedId)
                    .then(async () => {
                        console.log(` Connected with connectionId ${connectionId} and selectedId ${selectedId}`);
                        alertConnected(store);
                        await backfillMissedMessagesAsync(store, selectedId);
                    })
                    .catch((err: unknown) => {
                        console.error('Error adding client to group:', err);
                    });
            }
        }
    });
};

/**
 * Returns true when an incoming status event belongs to a previous run on this chat and
 * should be dropped before it can mutate state. Rules (#1564):
 *
 *   - Server omitted runId (older deploy, or runId stripped by SignalR null-handling): keep
 *     the event — fall through to the legacy cancel-state guard. This is the back-compat
 *     branch and covers SignalR delivering `null` for the optional Guid? parameter.
 *   - No active run locally (sibling-tab passive viewer, or activeRunId already cleared by
 *     a terminal status): keep the event — we have nothing to compare against.
 *   - Both ids present and different: drop. This is the only case where the event is
 *     known-stale.
 *
 * Exported for #1569 review-test (frontend filter behaviour pinned independently of the
 * full SignalR wiring).
 */
export const isStaleRunStatus = (runId: string | null | undefined, activeRunId: string | undefined): boolean =>
    typeof runId === 'string' && typeof activeRunId === 'string' && runId !== activeRunId;

// The first start runs on the reconnect schedule too: a customer who opens or reloads the chat during
// a backend restart needs the same window as one whose socket dropped.
const MAX_START_ATTEMPTS = RECONNECT_DELAYS_MS.length + 1;

// Roughly the whole start sequence before it moved to the reconnect schedule, so a revoked session is
// still noticed about as fast as it was then.
const PROBE_STALE_MS = 20_000;

/**
 * @param afterGiveUp - The previous connection gave up, on its start or its reconnects, so this one
 * covers a gap even when its own first attempt succeeds.
 */
const startSignalRConnection = async (
    hubConnection: signalR.HubConnection,
    store: StoreMiddlewareAPI,
    afterGiveUp: boolean,
) => {
    registerCommonSignalConnectionEvents(hubConnection, store);

    let retryCount = 0;
    let sessionState: SessionProbeOutcome | undefined;
    let probedAt = 0;
    for (;;) {
        try {
            await hubConnection.start();
            console.log('SignalR connection established');

            // After successful connection, add client to group if there's a selected conversation
            const selectedId = store.getState().conversations.selectedId;
            let joinedChatId: string | undefined;
            if (selectedId) {
                try {
                    await hubConnection.invoke('AddClientToGroupAsync', selectedId);
                    joinedChatId = selectedId;
                    console.log(
                        `Start, Connected with connectionId ${hubConnection.connectionId ?? ''} and selectedId ${selectedId}`,
                    );
                } catch (groupErr: unknown) {
                    console.error('Error adding client to group:', groupErr);
                }
            }

            // Nothing was delivered while the earlier attempts failed, the same gap onreconnected covers.
            if (retryCount > 0 || afterGiveUp) {
                // Attempts before the final one fail silently, so only the alert a connection that gave
                // up left behind needs taking back.
                if (store.getState().app.alerts.some((alert) => alert.id === Constants.app.CONNECTION_ALERT_ID)) {
                    alertConnected(store);
                }
                if (joinedChatId) {
                    // Not awaited: the relayed action that brought the connection up, often the
                    // customer's next message, must not wait on the fetch.
                    void backfillMissedMessagesAsync(store, joinedChatId, true);
                }
            }
            return;
        } catch (err) {
            console.error(
                `SignalR Connection Error (attempt ${String(retryCount + 1)}/${String(MAX_START_ATTEMPTS)}):`,
                err,
            );

            const delay = nextReconnectDelayMs(retryCount);
            const isFinalAttempt = delay === null;

            // Asked up front, again once the last answer is PROBE_STALE_MS old, and on the attempt
            // whose answer the user actually sees. Probing every attempt would add a round-trip to
            // each retry, but a single early answer goes stale over the ~2.5 min the sequence spans:
            // accessTokenFactory rereads the store per attempt, so silent renewal can replace an
            // expired token, and a session can be revoked after a first probe accepted it. The
            // re-ask keeps a revocation from waiting for the final attempt to trigger the logout.
            if (sessionState === undefined || isFinalAttempt || Date.now() - probedAt >= PROBE_STALE_MS) {
                const fresh = await probeSessionState(store);
                probedAt = Date.now();
                // A probe that could not reach the backend knows nothing, so it must not erase an
                // earlier definitive answer: overwriting `unauthorized` with `unknown` would report a
                // dead session as a network fault and skip the logout.
                if (sessionState === undefined || fresh !== 'unknown') {
                    sessionState = fresh;
                }
            }

            // A revoked session fails the upgrade with 401, which a raw WebSocket hides from JS.
            // Retrying it would only delay the logout the user actually needs. An expired token
            // still gets the remaining attempts — silent renew may land during them.
            if (sessionState === 'revoked') {
                await handleSessionProbeOutcome(store, sessionState);
                return;
            }

            if (isFinalAttempt) {
                if (!(await handleSessionProbeOutcome(store, sessionState))) {
                    store.dispatch(
                        addAlert({
                            message: t('signalR.unableToConnect'),
                            type: AlertType.Error,
                            id: Constants.app.CONNECTION_ALERT_ID,
                        }),
                    );
                }
                console.error('Max retry attempts reached. SignalR connection failed.');
                throw err;
            }

            retryCount++;
            await new Promise((resolve) => setTimeout(resolve, delay));
        }
    }
};

const registerSignalREvents = (hubConnection: signalR.HubConnection, store: StoreMiddlewareAPI) => {
    hubConnection.on(SignalRCallbackMethods.ReceiveMessage, (chatId: string, _: string, message: IChatMessage) => {
        store.dispatch({ type: 'conversations/addMessageToConversationFromServer', payload: { chatId, message } });
    });

    hubConnection.on(SignalRCallbackMethods.ReceiveMessageUpdate, (message: IChatMessage) => {
        const {
            chatId,
            id: messageId,
            content,
            contentMetadataArray,
            graphData,
            attachments,
            datasets,
            style,
            showCsatWidget,
            csatTrigger,
            csatSubmitted,
            spirisPrompt,
        } = message;
        // If tokenUsage is defined, that means full message content has already been streamed and updated from server. No need to update content again.
        store.dispatch({
            type: 'conversations/updateMessageProperty',
            payload: {
                chatId,
                messageIdOrIndex: messageId,
                property: message.tokenUsage ? 'tokenUsage' : 'content',
                value: message.tokenUsage ?? content,
                frontLoad: true,
                contentMetadataArray,
                graphData,
                attachments,
                datasets,
                style,
            },
        });
        store.dispatch(
            updateMessageProperty({
                chatId: chatId,
                messageIdOrIndex: message.id as string,
                property: 'followUpAsks',
                value: message.followUpAsks,
                frontLoad: true,
                contentMetadataArray,
                graphData,
                attachments,
                datasets,
                style,
            }),
        );
        // #2638: a LiveChat-resolution widget renders without the customer's EnableInChatCsat
        // (inlineCsatMessage), so without its trigger it would only show up after a reload.
        if (csatTrigger !== undefined) {
            store.dispatch(
                updateMessageProperty({
                    chatId,
                    messageIdOrIndex: messageId as string,
                    property: 'csatTrigger',
                    value: csatTrigger,
                }),
            );
        }
        // #1641: the inline CSAT widget toggle rides on this same update event when the
        // workflow or LiveChat path flips it server-side. Mirror the server's authoritative
        // value (true OR false) so a server-side clear — e.g. debug:resetcsat sending
        // showCsatWidget=false — reaches Redux too; the old `if (showCsatWidget)` only ever
        // wrote `true`, leaving a cleared widget stuck visible. The `!== undefined` guard just
        // skips any payload that omits the field entirely.
        if (showCsatWidget !== undefined) {
            store.dispatch(
                updateMessageProperty({
                    chatId,
                    messageIdOrIndex: messageId as string,
                    property: 'showCsatWidget',
                    value: showCsatWidget,
                }),
            );
        }
        // #1767: when the user answers or dismisses the inline CSAT widget in one tab, the server
        // persists csatSubmitted and relays it here so every OTHER open tab of the chat collapses
        // the widget live (no reload). Mirror the server's authoritative value (true OR false) the
        // same way showCsatWidget is mirrored above — so a server-side reset (debug:resetcsat sends
        // csatSubmitted=false) propagates too rather than leaving the widget stuck hidden. The
        // acting tab also receives this and re-applies its already-local value, which is idempotent.
        if (csatSubmitted !== undefined) {
            store.dispatch(
                updateMessageProperty({
                    chatId,
                    messageIdOrIndex: messageId as string,
                    property: 'csatSubmitted',
                    value: csatSubmitted,
                }),
            );
        }
        // The Spiris card arrives on the finalizing update, and its status flips (approved / denied /
        // expired) are relayed the same way so every open tab locks the card.
        if (spirisPrompt !== undefined) {
            store.dispatch(
                updateMessageProperty({
                    chatId,
                    messageIdOrIndex: messageId as string,
                    property: 'spirisPrompt',
                    value: spirisPrompt,
                }),
            );
        }
    });

    hubConnection.on(
        SignalRCallbackMethods.ReceiveBotResponseStatus,
        (chatId: string, status: string, statusMessage: string | undefined, runId: string | null | undefined) => {
            // Status can arrive for a chat that isn't in local state (not yet loaded, or removed).
            // The `Record<string, ChatState>` index signature lies about possible `undefined`, so
            // re-type the lookup and bail before reading properties to avoid a runtime crash.
            const conversations = store.getState().conversations.conversations as Record<string, ChatState | undefined>;
            const conversation = conversations[chatId];
            if (!conversation) {
                return;
            }

            // Stale-run filter (#1564) — see isStaleRunStatus below for the rules.
            if (isStaleRunStatus(runId, conversation.activeRunId)) {
                return;
            }

            // Terminal status emitted by ChatController when a request is aborted via the
            // stop button (#1554). Same payload as the local thunk so sibling tabs converge.
            if (status === 'cancelled') {
                store.dispatch({
                    type: 'conversations/cancelInFlightRequest',
                    payload: { chatId },
                });
                return;
            }
            // After a user-initiated cancel, AF may still flush one or two queued status
            // events before the 'cancelled' terminal arrives. If the local conversation is
            // no longer in flight (disabled=false, botResponseStatus=undefined), ignore
            // them — otherwise they'd re-enable the disabled state and clear the textarea
            // mid-handshake (#1554). Kept as a fallback for the back-compat case where
            // the server didn't tag the event with a runId.
            if (!conversation.disabled && conversation.botResponseStatus === undefined) {
                return;
            }
            store.dispatch({
                type: 'conversations/updateBotResponseStatus',
                payload: { chatId, status, statusMessage },
            });
        },
    );

    // Proactive human-handoff offer: the backend decided (out of band) to surface an offer card
    // on a bot message. Reload survival is handled separately by the offer fetch; this push just
    // makes the card appear live in the open chat.
    hubConnection.on(SignalRCallbackMethods.ReceiveEscalationOffer, (chatId: string, messageId: string) => {
        store.dispatch(setEscalationOffer({ chatId, messageId }));
    });

    // Agent typing indicator (#1770): a human agent started/stopped typing. Carries only chatId +
    // a bool; the UI shows the moving dots in the bottom status slot (ChatStatus), with a
    // receiver-side safety timeout there in case a "stopped" signal is lost.
    hubConnection.on(SignalRCallbackMethods.ReceiveAgentTypingState, (chatId: string, isTyping: boolean) => {
        store.dispatch(setAgentTyping({ chatId, isTyping }));
    });

    // Screenshot request: the handling agent asked the customer for a screenshot. Carries
    // only the chatId; ChatInput surfaces the share/attach prompt while the request is pending.
    hubConnection.on(SignalRCallbackMethods.ReceiveScreenshotRequest, (chatId: string) => {
        store.dispatch(setScreenshotRequest({ chatId }));
    });

    hubConnection.on(SignalRCallbackMethods.ReceiveAgentImage, (chatId: string, image: AgentImagePush) => {
        store.dispatch({
            type: 'conversations/addMessageToConversationFromServer',
            payload: { chatId, message: buildAgentImageMessage(chatId, image) },
        });
    });

    hubConnection.on(SignalRCallbackMethods.SessionLoggedOut, async (sessionId: string) => {
        if (store.getState().app.activeUserInfo?.sessionId === sessionId) {
            await AuthHelper.logoutForBlockedToken();
        }
    });
};

// This is a singleton instance of the SignalR connection
// eslint-disable-next-line prefer-const
let hubConnection: signalR.HubConnection | undefined = undefined;

// This function will return the singleton instance of the SignalR connection
let initializingHubConnection: Promise<signalR.HubConnection> | null = null;

let lastConnectionGaveUp = false;

// withAutomaticReconnect covers neither a connection whose start never succeeded nor one whose
// reconnect window ran out. Kept as the singleton, either would be handed to every later caller and the
// chat would get no pushes until a reload; cleared, the next relayed action starts over.
const forgetConnectionThatGaveUp = () => {
    lastConnectionGaveUp = true;
    hubConnection = undefined;
    initializingHubConnection = null;
};

export const getOrCreateHubConnection = (store: StoreMiddlewareAPI) => {
    if (process.env.NODE_ENV === 'development') {
        console.log('getOrCreateHubConnection', hubConnection !== undefined, initializingHubConnection !== null);
    }
    if (hubConnection !== undefined) {
        return Promise.resolve(hubConnection);
    }

    if (initializingHubConnection !== null) {
        return initializingHubConnection;
    }

    if (store.getState().app.authToken === null && AuthHelper.getAuthType() !== AuthType.None) {
        return null;
    }

    initializingHubConnection = (async () => {
        console.log('initializingHubConnection');
        const connection = setupSignalRConnectionToChatHub(store);
        hubConnection = connection;
        // onclose fires both when the reconnect window is used up and when the server closes the
        // connection for good. Only the first is forgotten. The server does the second right after the
        // handshake while it cannot read the deny list (TokenDenyHubFilter, #2182), and rebuilding then
        // would open a socket it closes again on every relayed action, with no backoff between them.
        let reconnecting = false;
        connection.onreconnecting(() => {
            reconnecting = true;
        });
        connection.onreconnected(() => {
            reconnecting = false;
        });
        connection.onclose(() => {
            if (reconnecting && hubConnection === connection) {
                forgetConnectionThatGaveUp();
            }
        });

        // Start the signalR connection to make sure messages are
        // sent to all clients and received by all clients
        try {
            await startSignalRConnection(connection, store, lastConnectionGaveUp);
            // A connection forgotten while its start was finishing has left a gap the next one must cover.
            if (hubConnection === connection) {
                lastConnectionGaveUp = false;
            }
        } catch (startError) {
            forgetConnectionThatGaveUp();
            throw startError;
        }
        registerSignalREvents(connection, store);
        return connection;
    })();

    return initializingHubConnection;
};
